import { useCallback } from 'react';

import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';

import { AssetTaxSitusFormValue } from './StructuredAssetsSubform.types';
import { AssetTaxSitusKindLabel } from './StructuredAssetsSubform.utils';

export type AssetTaxSitusEventAction = 'created' | 'updated' | 'deleted';

export function getAssetTaxSitusEventAction(
  previous: AssetTaxSitusFormValue | null | undefined,
  next: AssetTaxSitusFormValue | null | undefined
): AssetTaxSitusEventAction | null {
  const prev = previous ?? '';
  const nxt = next ?? '';
  if (prev === nxt) return null;
  if (!prev && nxt) return 'created';
  if (prev && !nxt) return 'deleted';
  return 'updated';
}

// Decides which (if any) tax-situs analytics event to emit when the user
// submits AssetDetailsModal.
//
// The form pre-fills the situs with an autofilled default whenever the asset
// has no persisted situs (true for both new assets and existing assets that
// were never assigned one). To avoid phantom 'created' events when the user
// just clicks Save without touching the autofill, we suppress the event if
// the submitted value is identical to what the form started with.
//
// When the user did make a change, the event action is classified against
// the actually-persisted situs (`persistedSitus`), not the autofill: e.g.
// changing 'g:client-1' (autofill) to 's:CA' on an asset with no persisted
// situs is still a 'created' event, not 'updated'.
export function getAssetDetailsModalSitusEventAction(args: {
  initialSitus: AssetTaxSitusFormValue;
  persistedSitus: AssetTaxSitusFormValue;
  currentSitus: AssetTaxSitusFormValue;
}): AssetTaxSitusEventAction | null {
  const { initialSitus, persistedSitus, currentSitus } = args;
  if (currentSitus === initialSitus) return null;
  return getAssetTaxSitusEventAction(persistedSitus, currentSitus);
}

// `source` is closed over the surfaces that emit this event so PostHog event
// grouping doesn't drift if a new call site picks an inconsistent label.
export type AssetTaxSitusEventSource =
  | 'add_holdings'
  | 'asset_details_modal'
  | 'edit_asset_side_drawer'
  | 'integrated_assets_inline'
  | 'short_form_create'
  | 'structured_assets_modal'
  | 'structured_assets_modal_inline';

// Properties for the `entity asset tax situs <action>` PostHog event.
// `householdId` + `situsKind` are required so the launch KPI ("% of
// households with real/TPP that have at least one situs entry logged",
// T1-5956) can be computed without joining to other sources.
// `entityId` / `assetClass` are best-effort — some call sites (e.g. the
// generic AssetDetailsModal) don't have them in scope.
export type AssetTaxSitusEventProperties = {
  source: AssetTaxSitusEventSource;
  householdId: string | null;
  situsKind: AssetTaxSitusKindLabel;
  entityId?: string;
  assetClass?: string;
  entityType?: string;
};

export function useTrackAssetTaxSitusEvent() {
  const trackUserEvent = useTrackUserEvent();
  return useCallback(
    (
      action: AssetTaxSitusEventAction,
      properties: AssetTaxSitusEventProperties
    ) => {
      trackUserEvent(`entity asset tax situs ${action}`, properties);
    },
    [trackUserEvent]
  );
}
